'use client';

import React, { isValidElement, type ReactNode, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  Breadcrumbs as BreadcrumbsWrapper,
  BreadcrumbsEllipsis,
  BreadcrumbsList,
  BreadcrumbsPage,
  BreadcrumbsItem,
  BreadcrumbsLink,
  BreadcrumbsSeparator,
} from '@packages/shared/src/ui/Breadcrumbs/Breadcrumbs';
import { Link } from '../Link/Link';
import { breadcrumbsMessages } from './messages';

type BreadcrumbsParentItemsProps = {
  name: string;
  href: string | null;
};
type BreadcrumbsCurrentItemProps = {
  name: string;
};
export type BreadcrumbsItemProps = [...BreadcrumbsParentItemsProps[], BreadcrumbsCurrentItemProps];

export type BreadcrumbsProps = {
  items: BreadcrumbsItemProps;
  lastItemAs?: 'span' | 'h1';
  'aria-label'?: string;
};

const BreadCrumbsWithSeparators = ({ children }: { children: ReactNode }) =>
  React.Children.toArray(children).reduce<ReactNode[]>((nodes, currentNode) => {
    if (nodes.length) {
      const key = isValidElement(currentNode) && currentNode.key;
      nodes.push(<BreadcrumbsSeparator key={`separator-${key}`} />);
    }
    nodes.push(currentNode);
    return nodes;
  }, []);

const isLinkItem = (
  item: BreadcrumbsParentItemsProps | BreadcrumbsCurrentItemProps,
): item is BreadcrumbsParentItemsProps => 'href' in item;

export const Breadcrumbs = ({ items, lastItemAs = 'span', ...props }: BreadcrumbsProps) => {
  const { formatMessage } = useIntl();
  const [expanded, setExpanded] = useState<boolean>(false);
  // All breadcrumb items with hrefs ( excluding the current page without href, in nl tennats, can be an empty array)
  const linkItems = items.slice(0, -1).filter(isLinkItem);
  const firstItem = linkItems[0];
  // middle breadcrumbs items with hrefs (no need of breadcrumbsTailCount to track), can be an empty array if the linkItems are less than 3.
  const middleItems = linkItems.slice(1, -1);
  // secondlast breadcrumb item with href and only exist if there are atleast two or more linkItems (covers nl tennants where breadcrumbs linkitems can be 0)
  const secondLastItem = linkItems.length >= 2 ? linkItems[linkItems.length - 1] : undefined;
  // last breadcrumb item (without a link)
  const lastItem = items[items.length - 1];

  return (
    <BreadcrumbsWrapper {...props}>
      <BreadcrumbsList>
        <BreadCrumbsWithSeparators>
          {linkItems.length > 0 && (
            <BreadcrumbsItem>
              {firstItem.href && (
                <BreadcrumbsLink asChild>
                  <Link href={firstItem.href}>{firstItem.name}</Link>
                </BreadcrumbsLink>
              )}
            </BreadcrumbsItem>
          )}
          {middleItems.length > 0 &&
            (expanded ? (
              middleItems.map(
                (item) =>
                  item.href && (
                    <BreadcrumbsItem key={item.href}>
                      <BreadcrumbsLink asChild>
                        <Link href={item.href}>{item.name}</Link>
                      </BreadcrumbsLink>
                    </BreadcrumbsItem>
                  ),
              )
            ) : (
              <BreadcrumbsItem>
                <BreadcrumbsEllipsis
                  aria-label={formatMessage(breadcrumbsMessages.showBreadcrumbsPath)}
                  onClick={() => setExpanded(true)}
                />
              </BreadcrumbsItem>
            ))}
          {secondLastItem && (
            <BreadcrumbsItem>
              {secondLastItem.href && (
                <BreadcrumbsLink asChild>
                  <Link href={secondLastItem.href}>{secondLastItem.name}</Link>
                </BreadcrumbsLink>
              )}
            </BreadcrumbsItem>
          )}
          <BreadcrumbsItem>
            <BreadcrumbsPage lastItemAs={lastItemAs}>{lastItem.name}</BreadcrumbsPage>
          </BreadcrumbsItem>
        </BreadCrumbsWithSeparators>
      </BreadcrumbsList>
    </BreadcrumbsWrapper>
  );
};
